import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { Currency } from '../../models/serializable/currency.js';
import { OnlineEventTicketOrder } from '../../models/serializable/online-event-ticket-order.js';
import { Price } from '../../models/serializable/price.js';
import { TicketOrderStatus } from '../../models/serializable/ticket-order-status.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { DiscountHelper } from '../discount-helper.js';
import { TicketPricing } from '../ticket-pricing.js';
import { IOrderPriceBreakdown, OnlineTicketOrderPriceBreakdown } from './order-price-breakdown.js';
import { TicketOrderDiscountMode } from './ticket-order-discount-mode.js';
import { IPaymentHelperAdapter } from './payment-helper-adapter.js';
import {
  IPaymentHelperConfig, isTicketOrderIncomeConfig, isTicketOrderRefundConfig,
} from './payment-helper-config.js';
import { ArgumentException } from '../../exceptions.js';
import { LegacyOrderDiscountBreakdown, LegacyOrderDiscountBreakdownEntry } from './discount-breakdown.js';
import { LegacyOrderFeeBreakdown, LegacyOrderFeeBreakdownEntry } from './fee-breakdown.js';
import { FeeHelper } from '../fee-helper.js';

export class OnlineTicketOrderPaymentHelper extends IPaymentHelperAdapter {
  public readonly orderTypeId: string = OnlineEventTicketOrder.TYPE_ID;

  static verifyType(dm: IPaymentHelperAdapter): boolean {
    return dm instanceof OnlineTicketOrderPaymentHelper;
  }

  static assertType(dm: IPaymentHelperAdapter): dm is OnlineTicketOrderPaymentHelper {
    return dm instanceof OnlineTicketOrderPaymentHelper;
  }

  getCurrencyOfOrder(order: CustomerOrderBase): Currency | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return order.tickets.length > 0 ? order.tickets[0].template.price.currency : null;
  }

  calculateDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculateDiscountForOnlineTicketOrder(order);
  }

  calculateCheckoutPriceBreakdownForOnlineOrder(order: CustomerOrderBase): IOrderPriceBreakdown | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const totalOrderPrice = this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: true,
      applySupplements: true,
      applyDonation: true,
      applyFee: true,
      discountMode: 'Everything',
    });

    const cleanOrderPrice = this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: false,
      applySupplements: false,
      applyDonation: false,
      applyFee: false,
      discountMode: 'Everything',
    });

    const orderPriceWithProducts = this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: true,
      applySupplements: false,
      applyDonation: false,
      applyFee: false,
      discountMode: 'Everything',
    });

    const orderPriceWithSupplements = this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: false,
      applySupplements: true,
      applyDonation: false,
      applyFee: false,
      discountMode: 'Everything',
    });

    if(totalOrderPrice == null || cleanOrderPrice == null ||
          orderPriceWithProducts == null || orderPriceWithSupplements == null
    ) {
      return null;
    }

    const productPrice = orderPriceWithProducts.compareTo(cleanOrderPrice) > 0 ?
        orderPriceWithProducts.difference(cleanOrderPrice):
        null;

    const supplementPrice = orderPriceWithSupplements.compareTo(cleanOrderPrice) > 0 ?
        orderPriceWithSupplements.difference(cleanOrderPrice):
        null;

    const donationPrice = order.donation?.clone() ?? null;
    const totalFeePrice = order.getTotalFee();
    const standardFeePrice = order.standardApplicationFee?.clone() ?? null;
    const productFeePrice = order.productApplicationFee?.clone() ?? null;

    return OnlineTicketOrderPriceBreakdown.create({
      totalPrice: totalOrderPrice,
      itemPrice: cleanOrderPrice,
      productPrice,
      supplementPrice,
      standardFeePrice,
      productFeePrice,
      totalFeePrice,
      donationPrice,
    });
  }

  calculateDiscountBreakdownForOrder(order: CustomerOrderBase): LegacyOrderDiscountBreakdown | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const pricing = TicketPricing.calculate(order);
    if(pricing == null) return null;
    const entries: LegacyOrderDiscountBreakdownEntry[] = [];

    for(let ti = 0; ti < pricing.tickets.length; ti++) {
      const ticketPricing = pricing.tickets[ti];
      if(DiscountHelper.hasDiscountValue(ticketPricing.ticket.discount)) {
        entries.push({ price: ticketPricing.ticketDiscount, host: { type: 'ticket', index: ti } });
      }
    }

    if(DiscountHelper.hasDiscountValue(order.discount)) {
      entries.push({ price: pricing.groupDiscountTotal, host: { type: 'order' } });
    }

    return { entries };
  }

  calculateFeeBreakdownForOrder(order: CustomerOrderBase): LegacyOrderFeeBreakdown | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const entries: LegacyOrderFeeBreakdownEntry[] = [];

    for(let ti = 0; ti < order.tickets.length; ti++) {
      const ticket = order.tickets[ti];
      const result = FeeHelper.calculateFeeValueForEventTicket(ticket);

      if(result != null) {
        entries.push({ ...result, host: { type: 'ticket', index: ti } });
      }
    }

    for(let pi = 0; pi < order.products.length; pi++) {
      const orderItem = order.products[pi];
      const result = FeeHelper.calculateFeeValueForProductOrderItem(orderItem, { applyScaling: false });

      if(result != null) {
        entries.push({ ...result, host: { type: 'product', index: pi } });
      }
    }

    return { entries };
  }

  calculateCheckoutPriceForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: true,
      applySupplements: true,
      applyDonation: true,
      applyFee: true,
      discountMode: 'Everything',
    });
  }

  calculateCheckoutPriceForDiscountProvider(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    // by default discounts applied to the order only take into account the tickets, not the products
    let includeProducts: boolean = false;
    let includeSupplements: boolean = false;

    if(isTicketOrderIncomeConfig(config)) {
      includeProducts = config.applyProducts;
      includeSupplements = config.applySupplements;
    }

    return this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: includeProducts,
      applySupplements: includeSupplements,
      applyDonation: false,
      applyFee: false,
      discountMode: 'TicketsOnly',
    });
  }

  calculateIncomeForOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderIncomeConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: config.applyProducts,
      applySupplements: config.applySupplements,
      applyDonation: false,
      applyFee: false,
      discountMode: 'Everything',
    });
  }

  calculateIncomeWithoutDiscountForOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderIncomeConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculatePriceForOnlineTicketOrder({
      order,
      applyProducts: config.applyProducts,
      applySupplements: config.applySupplements,
      applyDonation: false,
      applyFee: false,
      discountMode: 'None',
    });
  }

  canCreateRefundForOnlineOrder(order: CustomerOrderBase): boolean | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.canCreateRefundForOnlineTicketOrder(order);
  }

  calculateExistingRefundForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculateExistingRefundForOnlineTicketOrder(order);
  }

  calculateRemainingRefundForOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderRefundConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculateRemainingRefundForOnlineTicketOrder(order, config);
  }

  calculateMaxRefundForOnlineOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!OnlineEventTicketOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isTicketOrderRefundConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculateMaxRefundForOnlineTicketOrder(order, config);
  }

  private calculateDiscountForOnlineTicketOrder(order: OnlineEventTicketOrder): Price | null {
    if(isNullOrUndefined(order.discount)) {
      return null;
    }

    return TicketPricing.calculate(order)?.groupDiscountTotal ?? null;
  }

  private calculatePriceForOnlineTicketOrder({ order, applyProducts, applySupplements, applyFee, applyDonation, discountMode }: {
    order: OnlineEventTicketOrder,
    applyProducts: boolean,
    applySupplements: boolean,
    applyFee: boolean,
    applyDonation: boolean,
    discountMode: TicketOrderDiscountMode,
  }): Price | null {
    const currency = this.getCurrencyOfOrder(order);
    if(isNullOrUndefined(currency)) {
      return null;
    }

    const ticketPricing = TicketPricing.calculate(order, discountMode);
    if(ticketPricing == null) return null;
    let orderPrice = ticketPricing.total;

    if(applyProducts) {
      for(const item of order.products) {
        if(item.product.price.currency !== currency) {
          return null;
        }

        orderPrice = orderPrice.add(item.product.price.scale(item.quantity));
      }
    }

    if(applySupplements) {
      for(const ticket of order.tickets) {
        if(ticket.supplement == null) {
          continue;
        }
        if(ticket.supplement.template.price.currency !== currency) {
          return null;
        }

        const supplementPrice = ticket.supplement.template.price;
        orderPrice = orderPrice.add(supplementPrice);
      }
    }

    if(applyFee) {
      if(isNotNullOrUndefined(order.standardApplicationFee)) {
        orderPrice = orderPrice.add(order.standardApplicationFee);
      }
      if(isNotNullOrUndefined(order.productApplicationFee)) {
        orderPrice = orderPrice.add(order.productApplicationFee);
      }
    }

    if(applyDonation && isNotNullOrUndefined(order.donation)) {
      orderPrice = orderPrice.add(order.donation);
    }

    return orderPrice;
  }

  private canCreateRefundForOnlineTicketOrder(order: OnlineEventTicketOrder): boolean {
    if(order.status !== TicketOrderStatus.Refunded) {
      return false;
    }

    return order.refunds.length <= 0;
  }

  private calculateExistingRefundForOnlineTicketOrder(order: OnlineEventTicketOrder): Price | null {
    let currency: Currency | null = null;
    if(order.tickets.length > 0) {
      currency = order.tickets[0].template.price.currency;
    }

    if(isNullOrUndefined(currency)) {
      return null;
    }

    let totalRefundSoFar = Price.zero(currency);
    for(const refund of order.refunds) {
      totalRefundSoFar = totalRefundSoFar.add(refund.price);
    }

    return totalRefundSoFar;
  }

  private calculateRemainingRefundForOnlineTicketOrder(
      order: OnlineEventTicketOrder,
      options: {
          includeStandardFee: boolean,
          includeProductFee: boolean,
          includeProducts: boolean,
          includeSupplements: boolean,
        }
  ): Price | null {
    let orderPrice = this.calculateIncomeForOrder(
        order,
        { applyProducts: options.includeProducts, applySupplements: options.includeSupplements }
    );

    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    if(order.standardApplicationFee != null && options.includeStandardFee) {
      orderPrice = orderPrice.add(order.standardApplicationFee);
    }

    if(order.productApplicationFee != null && options.includeProductFee) {
      orderPrice = orderPrice.add(order.productApplicationFee);
    }

    let totalRefundSoFar = Price.zero(orderPrice.currency);
    for(const refund of order.refunds) {
      totalRefundSoFar = totalRefundSoFar.add(refund.price);
    }

    return orderPrice.difference(totalRefundSoFar);
  }

  private calculateMaxRefundForOnlineTicketOrder(
      order: OnlineEventTicketOrder,
      options: {
          includeStandardFee: boolean,
          includeProductFee: boolean,
          includeProducts: boolean,
          includeSupplements: boolean,
        }
  ): Price | null {
    let orderPrice = this.calculateIncomeForOrder(
        order,
        { applyProducts: options.includeProducts, applySupplements: options.includeSupplements }
    );

    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    if(order.standardApplicationFee != null && options.includeStandardFee) {
      orderPrice = orderPrice.add(order.standardApplicationFee);
    }

    if(order.productApplicationFee != null && options.includeProductFee) {
      orderPrice = orderPrice.add(order.productApplicationFee);
    }

    return orderPrice;
  }
}
